'use client'

import * as React from 'react'

import Link from 'next/link'
import { motion } from 'framer-motion'

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/Tooltip'

export default function AboutPage() {
  return (
    <div className="max-w-3xl px-6 mx-auto">
      <h1 className="mb-8 text-4xl font-semibold lg:text-5xl lg:mb-12">
        About
      </h1>
      <TooltipProvider>
        <div className="flex flex-col gap-6 mb-16 text-lg leading-8 lg:text-xl text-gray-1100 lg:leading-9">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.2,
              duration: 0.4,
              type: 'tween',
              ease: 'easeInOut',
            }}
          >
            I&apos;m a lead software engineer with a passion for building data
            intensive products. I&apos;m currently the Head of Engineering at{' '}
            <Tooltip>
              <TooltipTrigger>
                <Link
                  href="https://www.behaviourlab.com"
                  className="font-normal text-indigo-1000 dark:text-mint-1100"
                >
                  Behaviour Lab
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p className="w-48">
                  I joined Behaviour Lab in my first full-time role in 2021,
                  where I learned how fun it is to work at a start up. The
                  ambiguity, the constant learning, the autonomy, the
                  responsiblity - there isn&apos;t a single place where
                  you&apos;d learn more.
                </p>
              </TooltipContent>
            </Tooltip>
            {', '}
            where I&apos;m building a disruptive decision support system that
            helps asset managers make better investment decisions through
            analytics based on historical portfolio and trade data, real-time
            nudging systems and a powerful research archive.
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.3,
              duration: 0.4,
              type: 'tween',
              ease: 'easeInOut',
            }}
          >
            In the last two years, I&apos;ve{' '}
            <Tooltip>
              <TooltipTrigger>
                <span className="font-normal text-indigo-1000 dark:text-mint-1100">
                  built our team
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p className="w-48">
                  I was one of the first engineers on the team and have since
                  taken on a much broader role in shaping our products,
                  workflows, and entire engineering culture.
                </p>
              </TooltipContent>
            </Tooltip>{' '}
            from 3 to 12 engineers and designers and integrated our solutions
            into asset managers with over hundreds of billions of dollars in
            assets under management. We&apos;re pioneering new ways to run
            distributed real-time analytics, visualize complex datasets and
            build a suite of powerful research tools that use UX design,
            engineering and behavioural science to improve the workflows of
            large asset managers.
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.4,
              duration: 0.4,
              type: 'tween',
              ease: 'easeInOut',
            }}
          >
            I&apos;ve been writing code for 6 years and have experience at
            organizations of all sizes from{' '}
            <Tooltip>
              <TooltipTrigger>
                <Link
                  href="https://www.americanexpress.com/"
                  className="font-normal text-indigo-1000 dark:text-mint-1100"
                >
                  large financial services company
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p className="w-min whitespace-nowrap">American Express</p>
              </TooltipContent>
            </Tooltip>
            , an{' '}
            <Tooltip>
              <TooltipTrigger>
                <Link
                  href="https://nextjump.com/"
                  className="font-normal text-indigo-1000 dark:text-mint-1100"
                >
                  american tech company
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p className="w-min whitespace-nowrap">NextJump</p>
              </TooltipContent>
            </Tooltip>{' '}
            to a{' '}
            <Tooltip>
              <TooltipTrigger>
                <Link
                  href="https://behaviourlab.com/"
                  className="font-normal text-indigo-1000 dark:text-mint-1100"
                >
                  small startup
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p className="w-min whitespace-nowrap">Behaviour Lab</p>
              </TooltipContent>
            </Tooltip>
            . I have a Masters degree in Electrical and Electronic Engineering
            from{' '}
            <Tooltip>
              <TooltipTrigger>
                <Link
                  href="https://imperial.ac.uk"
                  className="font-normal text-indigo-1000 dark:text-mint-1100"
                >
                  Imperial College London
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p className="w-44">
                  Imperial College London is a public research university in
                  London, England that specializes in science and technology.
                </p>
              </TooltipContent>
            </Tooltip>
            . I graduated with First Class Honours on the Dean&apos;s List (top
            10% of the class) and was the recipient of the Dennis Gabor Prize
            for my academic contributions to the department and student life.
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.5,
              duration: 0.4,
              type: 'tween',
              ease: 'easeInOut',
            }}
          >
            Outside of work, I&apos;ve built a range of projects from a
            probabilistic inference module to detect COVID-19 from blood
            pathalogy data, to a visualization and audit platform that measures
            and visualizes your digital carbon footprint with interactive maps,
            to a real time video chat application powered by Web RTC and AI to
            help candidates prepare for job interviews. Some of these projects
            won international hackathons and were featured in podcasts,
            conferences, and at{' '}
            <Tooltip>
              <TooltipTrigger>
                <Link
                  href="https://techcrunch.com/unified-video/special-announcement-by-esri"
                  className="font-normal text-indigo-1000 dark:text-mint-1100"
                >
                  TechCrunch Disrupt
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p className="w-44">
                  Check out the video for yourself where Esri CTO Euan Cameron
                  announces us the winner of their hackathon on the main stage
                  of TechCrunch Disrupt 2021.
                </p>
              </TooltipContent>
            </Tooltip>
            .
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.6,
              duration: 0.4,
              type: 'tween',
              ease: 'easeInOut',
            }}
          >
            I&apos;m not actively looking for new opportunities at this time but
            I&apos;m always open to interesting conversations. Feel free to
            reach out if you&apos;d like to chat!
          </motion.div>
        </div>
      </TooltipProvider>
    </div>
  )
}
